import React, { FC, Suspense, useMemo } from 'react';
import classnames from 'classnames';
import { ErrorBoundary, useRouter, useSignal } from '@funtech-int/rival-react';
import { PaymentPlace, useAnalytics, usePaymentPlace } from '@funtech-int/yango-shared';

import { Button } from '@/shared/components/Button/Button';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { useI18n } from '@/app/hooks/useI18n';
import { createAnalyticsEvents } from '@/app/lib/createAnalyticsEvents';
import { mainRoute } from '@/scenarios/home';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { useSendSuccessPurchaseEvent } from '@/shared/hooks/useSendSuccessPurchaseEvent';

import styles from './HeaderSubscriptionButton.module.css';

interface Props {
    className: string;
}

interface HeaderSubscriptionInternalProps extends Props {
    paymentPlace: PaymentPlace;
}

const HeaderSubscriptionComponent: FC<HeaderSubscriptionInternalProps> = ({
    className,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const router = useRouter();
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const offer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.Header,
    });

    const headerOfferButtonAnalytics = useAnalytics(createAnalyticsEvents);
    const buttonText = i18n('header__subscription_button_title');

    const sendSuccessPurchaseMetrikaEvent = useSendSuccessPurchaseEvent(offer);

    const controller = useMemo(() => {
        const analytics = headerOfferButtonAnalytics.createOfferAnalyticsEvents(offer, buttonText);
        function onPurchaseSuccess() {
            subscriptionRefetch();
            sendSuccessPurchaseMetrikaEvent();
            analytics.succeeded();

            router.navigate({ to: mainRoute() });
        }
        return {
            onOfferShow: () => {
                if (!offer) return;
                analytics.showed();
                paymentPlace.analytics.offerShowed(offer);
            },
            onOfferClick: async () => {
                if (!offer) return;
                analytics.navigated();
                paymentPlace.analytics.offerClicked(offer);

                await paymentPlace.checkout(offer, {
                    onPurchaseSuccess,
                });
            },
        };
    }, [
        subscriptionRefetch,
        sendSuccessPurchaseMetrikaEvent,
        offer,
        router,
        buttonText,
        headerOfferButtonAnalytics,
        paymentPlace,
    ]);

    if (!offer) {
        return null;
    }

    return (
        <Button
            className={classnames(className, styles.button)}
            size={32}
            theme="dark"
            variant="plus"
            onClick={controller.onOfferClick}
            onShow={controller.onOfferShow}
        >
            {buttonText}
        </Button>
    );
};

const HeaderSubscriptionPurchaseContainer = (props: Props) => {
    const page = usePaymentPage();

    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.Header],
    });

    if (!paymentPlace) {
        return null;
    }

    return <HeaderSubscriptionComponent {...props} paymentPlace={paymentPlace} />;
};

export const HeaderSubscriptionButton = (props: Props) => {
    const profileManager = useProfileManager();

    const hasSubscription = useSignal(profileManager.hasSubscription);

    if (hasSubscription) {
        return null;
    }

    return (
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <HeaderSubscriptionPurchaseContainer {...props} />
            </Suspense>
        </ErrorBoundary>
    );
};
